import {
  AssessmentExportButton,
  AssessmentSummaryPanel,
} from "@components/campaigns/stages/AssessmentSummaryPanel";
import { CandidateActivitySection } from "@components/candidates/CandidateActivityTimeline";
import { CandidateReferenceBadge } from "@components/candidates/CandidateReferenceBadge";
import { ProfileDataPanel } from "@components/candidates/ProfileDataPanel";
import { DrivePickerButton } from "@components/shared/DrivePickerButton";
import { FileDropZone } from "@components/shared/FileDropZone";
import { NotesPanel } from "@components/shared/NotesPanel";
import { RefreshIndicator } from "@components/shared/RefreshIndicator";
import {
  ANONYMIZE_CANDIDATE,
  ATTACH_CANDIDATE_RESUME,
  DELETE_CANDIDATE,
  DETACH_CANDIDATE_RESUME,
  EXPORT_CANDIDATE_DATA,
  UPDATE_CANDIDATE,
} from "@lib/graphql/mutations";
import { CANDIDATE_QUERY } from "@lib/graphql/queries";
import { reportError, runMutation } from "@lib/graphql/unwrapMutation";
import { useRecordChannel } from "@lib/hooks/useRecordChannel";
import {
  AlertTriangle,
  ArrowLeft,
  Download,
  ExternalLink,
  FileText,
  Pencil,
  Save,
  Trash2,
  Upload,
  X,
} from "lucide-react";
import { useCallback, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { useMutation, useQuery } from "urql";

export function CandidateDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [{ data, fetching }, reexecute] = useQuery({ query: CANDIDATE_QUERY, variables: { id } });
  const refresh = useCallback(() => reexecute({ requestPolicy: "network-only" }), [reexecute]);
  useRecordChannel("Candidate", id!, refresh);
  const [, updateCandidate] = useMutation(UPDATE_CANDIDATE);
  const [, attachResume] = useMutation(ATTACH_CANDIDATE_RESUME);
  const [, detachResume] = useMutation(DETACH_CANDIDATE_RESUME);
  const [, exportCandidateData] = useMutation(EXPORT_CANDIDATE_DATA);
  const [, anonymizeCandidate] = useMutation(ANONYMIZE_CANDIDATE);
  const [, deleteCandidate] = useMutation(DELETE_CANDIDATE);
  const [editing, setEditing] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [anonymizing, setAnonymizing] = useState(false);
  const [confirmAnonymize, setConfirmAnonymize] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [deletingResumeId, setDeletingResumeId] = useState<string | null>(null);

  const candidate = data?.candidate;

  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    linkedinUrl: "",
    currentRoleTitle: "",
    currentCompany: "",
    location: "",
    notes: "",
    consentStatus: "not_requested",
    consentNotes: "",
  });

  function startEditing() {
    if (!candidate) return;
    setForm({
      firstName: candidate.firstName || "",
      lastName: candidate.lastName || "",
      email: candidate.email || "",
      phone: candidate.phone || "",
      linkedinUrl: candidate.linkedinUrl || "",
      currentRoleTitle: candidate.currentRoleTitle || "",
      currentCompany: candidate.currentCompany || "",
      location: candidate.location || "",
      notes: candidate.notes || "",
      consentStatus: candidate.consentStatus || "not_requested",
      consentNotes: candidate.consentNotes || "",
    });
    setEditing(true);
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    const input: Record<string, string | undefined> = {
      id: id!,
      firstName: form.firstName,
      lastName: form.lastName,
      email: form.email || undefined,
      phone: form.phone || undefined,
      linkedinUrl: form.linkedinUrl || undefined,
      currentRoleTitle: form.currentRoleTitle || undefined,
      currentCompany: form.currentCompany || undefined,
      location: form.location || undefined,
      notes: form.notes || undefined,
      consentStatus: form.consentStatus,
      consentNotes: form.consentNotes || undefined,
    };
    const payload = await runMutation("saving the candidate", () => updateCandidate({ input }));
    if (payload) {
      setEditing(false);
      reexecute({ requestPolicy: "network-only" });
    }
  }

  async function handleResumeUpload(file: File) {
    if (!candidate) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);

      const csrfToken = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content;
      const response = await fetch("/uploads", {
        method: "POST",
        headers: csrfToken ? { "X-CSRF-Token": csrfToken } : {},
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`Upload failed (HTTP ${response.status}).`);
      }

      const uploadData = await response.json().catch(() => null);
      const signedId = uploadData?.signed_id;
      if (!signedId) {
        throw new Error("Upload response did not include a signed_id.");
      }

      await attachResumeBySignedId(signedId);
    } catch (error) {
      reportError(error, "uploading the resume");
    } finally {
      setUploading(false);
    }
  }

  async function attachResumeBySignedId(signedId: string) {
    if (!candidate) return;
    const payload = await runMutation("attaching the resume", () =>
      attachResume({ input: { candidateId: candidate.id, signedId } }),
    );
    if (payload) {
      reexecute({ requestPolicy: "network-only" });
    }
  }

  async function handleResumeDelete(attachmentId: string) {
    if (!candidate || !confirm("Delete this resume?")) return;
    setDeletingResumeId(attachmentId);
    try {
      const payload = await runMutation("deleting the resume", () =>
        detachResume({ input: { candidateId: candidate.id, attachmentId } }),
      );
      if (payload) {
        reexecute({ requestPolicy: "network-only" });
      }
    } finally {
      setDeletingResumeId(null);
    }
  }

  if (fetching && !candidate) return <p className="text-sm text-text-muted">Loading...</p>;
  if (!candidate) return <p className="text-sm text-text-muted">Candidate not found</p>;

  function handleBack() {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate("/candidates");
    }
  }

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <button
          type="button"
          onClick={handleBack}
          className="flex items-center gap-1 text-sm text-text-muted hover:text-text-secondary"
        >
          <ArrowLeft size={16} /> Back
        </button>
        <RefreshIndicator active={fetching} />
      </div>

      {editing ? (
        <form onSubmit={handleSave} className="rounded-lg bg-card p-6 shadow-sm ring-1 ring-ring">
          <h2 className="mb-4 text-lg font-medium text-text-primary">Edit Candidate</h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <Field
              label="First Name *"
              value={form.firstName}
              onChange={(v) => setForm({ ...form, firstName: v })}
              required
            />
            <Field
              label="Last Name *"
              value={form.lastName}
              onChange={(v) => setForm({ ...form, lastName: v })}
              required
            />
            <Field
              label="Email"
              value={form.email}
              onChange={(v) => setForm({ ...form, email: v })}
              type="email"
            />
            <Field
              label="Phone"
              value={form.phone}
              onChange={(v) => setForm({ ...form, phone: v })}
            />
            <Field
              label="LinkedIn URL"
              value={form.linkedinUrl}
              onChange={(v) => setForm({ ...form, linkedinUrl: v })}
            />
            <Field
              label="Current Role"
              value={form.currentRoleTitle}
              onChange={(v) => setForm({ ...form, currentRoleTitle: v })}
            />
            <Field
              label="Current Company"
              value={form.currentCompany}
              onChange={(v) => setForm({ ...form, currentCompany: v })}
            />
            <Field
              label="Location"
              value={form.location}
              onChange={(v) => setForm({ ...form, location: v })}
            />
          </div>
          <div className="mt-4">
            <label className="block text-sm font-medium text-text-secondary">Notes</label>
            <textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={4}
              className="mt-1 block w-full rounded-md border border-input-border bg-card px-3 py-2 text-sm text-text-primary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
            />
          </div>
          <div className="mt-6 border-t border-border pt-4">
            <h3 className="text-sm font-semibold text-text-primary">GDPR Consent</h3>
            <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="block text-sm font-medium text-text-secondary">
                  Consent Status
                </label>
                <select
                  value={form.consentStatus}
                  onChange={(e) => setForm({ ...form, consentStatus: e.target.value })}
                  className="mt-1 block w-full rounded-md border border-input-border bg-card px-3 py-2 text-sm text-text-primary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
                >
                  <option value="not_requested">Not Requested</option>
                  <option value="pending">Pending</option>
                  <option value="given">Given</option>
                  <option value="refused">Refused</option>
                  <option value="withdrawn">Withdrawn</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-text-secondary">
                  Consent Notes
                </label>
                <textarea
                  value={form.consentNotes}
                  onChange={(e) => setForm({ ...form, consentNotes: e.target.value })}
                  rows={2}
                  placeholder="e.g. Consent given via email on..."
                  className="mt-1 block w-full rounded-md border border-input-border bg-card px-3 py-2 text-sm text-text-primary placeholder:text-text-faint focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
                />
              </div>
            </div>
          </div>
          <div className="mt-4 flex gap-2">
            <button
              type="submit"
              className="flex items-center gap-2 rounded-md bg-accent px-4 py-2 text-sm font-medium text-white hover:bg-accent-hover"
            >
              <Save size={16} /> Save
            </button>
            <button
              type="button"
              onClick={() => setEditing(false)}
              className="flex items-center gap-2 rounded-md border border-input-border px-4 py-2 text-sm font-medium text-text-secondary hover:bg-card-hover"
            >
              <X size={16} /> Cancel
            </button>
          </div>
        </form>
      ) : (
        <>
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-text-primary">
                {candidate.fullName}
                {typeof candidate.referenceNumber === "number" && (
                  <CandidateReferenceBadge
                    referenceNumber={candidate.referenceNumber}
                    variant="detail"
                  />
                )}
              </h1>
              <div className="mt-1 flex flex-wrap gap-4 text-sm text-text-muted">
                {candidate.currentRoleTitle && <span>{candidate.currentRoleTitle}</span>}
                {candidate.currentCompany && <span>at {candidate.currentCompany}</span>}
                {candidate.location && <span>{candidate.location}</span>}
              </div>
            </div>
            <button
              onClick={startEditing}
              className="flex items-center gap-2 rounded-md border border-input-border px-3 py-2 text-sm font-medium text-text-secondary hover:bg-card-hover"
            >
              <Pencil size={16} /> Edit
            </button>
          </div>

          <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <InfoCard label="Email" value={candidate.email} />
            <InfoCard label="Phone" value={candidate.phone} />
            <InfoCard label="LinkedIn" value={candidate.linkedinUrl} href={candidate.linkedinUrl} />
          </div>

          <div className="mt-4 rounded-lg bg-card p-4 shadow-sm ring-1 ring-ring">
            <h3 className="text-sm font-medium text-text-secondary">GDPR Consent</h3>
            <div className="mt-2 flex flex-wrap items-center gap-3">
              <ConsentBadge status={candidate.consentStatus} />
              {candidate.consentGivenAt && (
                <span className="text-xs text-text-muted">
                  {new Date(candidate.consentGivenAt).toLocaleDateString()}
                </span>
              )}
            </div>
            {candidate.consentNotes && (
              <p className="mt-2 text-sm text-text-muted">{candidate.consentNotes}</p>
            )}
          </div>

          {candidate.notes && (
            <div className="mt-4 rounded-lg bg-card p-4 shadow-sm ring-1 ring-ring">
              <h3 className="text-sm font-medium text-text-secondary">Notes</h3>
              <p className="mt-1 whitespace-pre-wrap text-sm text-text-secondary">
                {candidate.notes}
              </p>
            </div>
          )}
        </>
      )}

      {/* Notes */}
      {candidate && (
        <div className="mt-6">
          <NotesPanel
            notableType="Candidate"
            notableId={candidate.id}
            notes={candidate.attachedNotes ?? []}
            onChanged={() => reexecute({ requestPolicy: "network-only" })}
          />
        </div>
      )}

      {/* Resumes */}
      <div className="mt-6">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-medium text-text-primary">Resumes</h2>
          <FileDropZone
            accept=".pdf"
            disabled={uploading}
            onFiles={(files) => handleResumeUpload(files[0])}
            overlayLabel="Drop resume here"
          >
            {({ openPicker }) => (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={openPicker}
                  disabled={uploading}
                  className="flex cursor-pointer items-center gap-2 rounded-md border border-input-border px-3 py-2 text-sm font-medium text-text-secondary hover:bg-card-hover disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Upload size={16} /> {uploading ? "Uploading..." : "Upload Resume"}
                </button>
                <DrivePickerButton
                  accept=".pdf"
                  disabled={uploading}
                  onSignedId={(signedId) => attachResumeBySignedId(signedId)}
                />
              </div>
            )}
          </FileDropZone>
        </div>
        {candidate.resumes?.length === 0 ? (
          <p className="mt-2 text-sm text-text-muted">No resumes uploaded</p>
        ) : (
          <div className="mt-2 space-y-2">
            {candidate.resumes?.map(
              (resume: { id: string; filename: string; url: string; createdAt: string }) => (
                <div
                  key={resume.id}
                  className="flex items-center gap-2 rounded-lg bg-card p-3 shadow-sm ring-1 ring-ring"
                >
                  <FileText size={16} className="text-text-muted" />
                  <a
                    href={resume.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-sm font-medium text-accent hover:underline"
                  >
                    {resume.filename} <ExternalLink size={12} />
                  </a>
                  <button
                    onClick={() => handleResumeDelete(resume.id)}
                    disabled={deletingResumeId === resume.id}
                    className="ml-auto rounded p-1 text-text-faint hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-950"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ),
            )}
          </div>
        )}
      </div>

      {/* Profile Data */}
      <ProfileDataPanel profileData={candidate.profileData} />

      {/* Campaigns */}
      <div className="mt-6">
        <h2 className="text-lg font-medium text-text-primary">Campaigns</h2>
        {candidate.campaigns?.length === 0 ? (
          <p className="mt-2 text-sm text-text-muted">Not assigned to any campaigns</p>
        ) : (
          <div className="mt-2 space-y-2">
            {candidate.campaigns?.map((campaign: { id: string; title: string; status: string }) => (
              <Link
                key={campaign.id}
                to={`/campaigns/${campaign.id}`}
                className="block rounded-lg bg-card p-3 shadow-sm ring-1 ring-ring transition-colors hover:bg-card-hover"
              >
                <span className="font-medium text-accent">{campaign.title}</span>
                <span className="ml-2 text-sm text-text-muted">{campaign.status}</span>
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* Assessments */}
      <CandidateAssessments
        candidate={{
          fullName: candidate.fullName,
          currentRoleTitle: candidate.currentRoleTitle ?? null,
          currentCompany: candidate.currentCompany ?? null,
          location: candidate.location ?? null,
        }}
        campaignCandidates={candidate.campaignCandidates ?? []}
      />

      {/* Activity */}
      <CandidateActivitySection candidateId={candidate.id} />

      {/* Data Management */}
      <div className="mt-8 rounded-lg border border-red-200 bg-red-50 p-6 dark:border-red-900/30 dark:bg-red-900/10">
        <h2 className="flex items-center gap-2 text-lg font-medium text-text-primary">
          <AlertTriangle size={20} className="text-red-500" /> Data Management
        </h2>
        <p className="mt-1 text-sm text-text-muted">
          Export, anonymize, or permanently delete this candidate&apos;s data. Anonymize preserves
          the record for compliance; delete removes it and all associated data (campaigns, talent
          pools, notes, resumes) and cannot be undone.
        </p>
        <div className="mt-4 flex flex-wrap gap-3">
          <button
            type="button"
            onClick={async () => {
              setExporting(true);
              const payload = await runMutation("exporting candidate data", () =>
                exportCandidateData({ input: { candidateId: id } }),
              );
              if (payload?.downloadUrl) window.open(payload.downloadUrl, "_blank");
              setExporting(false);
            }}
            disabled={exporting}
            className="flex items-center gap-2 rounded-md border border-input-border px-4 py-2 text-sm font-medium text-text-secondary hover:bg-card-hover disabled:opacity-50"
          >
            <Download size={16} /> {exporting ? "Exporting..." : "Export All Data"}
          </button>
          {confirmAnonymize ? (
            <span className="flex items-center gap-2">
              <button
                type="button"
                onClick={async () => {
                  setAnonymizing(true);
                  const payload = await runMutation("anonymizing the candidate", () =>
                    anonymizeCandidate({ input: { candidateId: id } }),
                  );
                  setAnonymizing(false);
                  if (payload) {
                    setConfirmAnonymize(false);
                    reexecute({ requestPolicy: "network-only" });
                  }
                }}
                disabled={anonymizing}
                className="rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
              >
                {anonymizing ? "Anonymizing..." : "Confirm Anonymize"}
              </button>
              <button
                type="button"
                onClick={() => setConfirmAnonymize(false)}
                className="rounded-md border border-input-border px-4 py-2 text-sm font-medium text-text-secondary hover:bg-card-hover"
              >
                Cancel
              </button>
            </span>
          ) : (
            <button
              type="button"
              onClick={() => setConfirmAnonymize(true)}
              className="flex items-center gap-2 rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700"
            >
              <Trash2 size={16} /> Anonymize Candidate
            </button>
          )}
          {confirmDelete ? (
            <span className="flex items-center gap-2">
              <button
                type="button"
                onClick={async () => {
                  setDeleting(true);
                  setDeleteError(null);
                  const result = await deleteCandidate({ input: { candidateId: id } });
                  const errors = result.data?.deleteCandidate?.errors ?? [];
                  if (result.error || errors.length > 0) {
                    setDeleteError(
                      result.error?.message ?? errors.join(", ") ?? "Failed to delete candidate",
                    );
                    setDeleting(false);
                    return;
                  }
                  navigate("/candidates");
                }}
                disabled={deleting}
                className="rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
              >
                {deleting ? "Deleting..." : "Confirm Delete"}
              </button>
              <button
                type="button"
                onClick={() => {
                  setConfirmDelete(false);
                  setDeleteError(null);
                }}
                disabled={deleting}
                className="rounded-md border border-input-border px-4 py-2 text-sm font-medium text-text-secondary hover:bg-card-hover disabled:opacity-50"
              >
                Cancel
              </button>
            </span>
          ) : (
            <button
              type="button"
              onClick={() => setConfirmDelete(true)}
              className="flex items-center gap-2 rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700"
            >
              <Trash2 size={16} /> Delete Candidate
            </button>
          )}
        </div>
        {deleteError && (
          <p className="mt-3 text-sm text-red-600 dark:text-red-400">{deleteError}</p>
        )}
      </div>
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  type = "text",
  required = false,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  required?: boolean;
}) {
  return (
    <div>
      <label className="block text-sm font-medium text-text-secondary">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        className="mt-1 block w-full rounded-md border border-input-border bg-card px-3 py-2 text-sm text-text-primary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
      />
    </div>
  );
}

interface CampaignCandidateAssessment {
  id: string;
  stage: string;
  campaign: {
    id: string;
    title: string;
    roleBlueprint: {
      id: string;
      capabilityDimensions: {
        id: string;
        continuityId: string;
        name: string;
        description: string | null;
        position: number;
        idealScore: number | null;
      }[];
    } | null;
  };
  candidateAssessment: {
    id: string;
    currentVersion: {
      id: string;
      overallScore: number | null;
      recommendation: string | null;
      overallSummary: string | null;
      strengths: string | null;
      concerns: string | null;
      culturalFitNotes: string | null;
      trajectoryNotes: string | null;
      riskNotes: string | null;
      versionNumber: number;
      effectiveFrom: string;
      assessor: { id: string; name: string };
      competencyScores: {
        id: string;
        score: number | null;
        anchorLabel: string | null;
        evidence: string | null;
        notes: string | null;
        detailedNotes: string | null;
        capabilityDimension: {
          id: string;
          continuityId: string;
          name: string;
          description: string | null;
          idealScore: number | null;
        };
      }[];
    } | null;
  } | null;
}

function CandidateAssessments({
  candidate,
  campaignCandidates,
}: {
  candidate: {
    fullName: string;
    currentRoleTitle: string | null;
    currentCompany: string | null;
    location: string | null;
  };
  campaignCandidates: CampaignCandidateAssessment[];
}) {
  const withAssessments = campaignCandidates.filter((cc) => cc.candidateAssessment?.currentVersion);

  if (withAssessments.length === 0) return null;

  return (
    <div className="mt-6">
      <h2 className="text-lg font-medium text-text-primary">Assessments</h2>
      <div className="mt-2 space-y-4">
        {withAssessments.map((cc) => {
          const version = cc.candidateAssessment!.currentVersion!;
          const dimensions = cc.campaign.roleBlueprint?.capabilityDimensions ?? [];

          return (
            <div key={cc.id} className="rounded-lg bg-card p-4 shadow-sm ring-1 ring-ring">
              <div className="mb-4 flex items-center justify-between border-b border-border pb-3">
                <Link
                  to={`/campaigns/${cc.campaign.id}`}
                  className="text-sm font-medium text-accent hover:underline"
                >
                  {cc.campaign.title}
                </Link>
                <span className="text-xs text-text-faint">Stage: {cc.stage}</span>
              </div>
              <AssessmentSummaryPanel
                candidate={candidate}
                assessment={version}
                dimensions={dimensions}
                headerActions={<AssessmentExportButton campaignCandidateId={cc.id} />}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}

function InfoCard({
  label,
  value,
  href,
}: {
  label: string;
  value?: string | null;
  href?: string | null;
}) {
  if (!value) return null;

  return (
    <div className="rounded-lg bg-card p-4 shadow-sm ring-1 ring-ring">
      <h3 className="text-sm font-medium text-text-secondary">{label}</h3>
      {href ? (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-1 flex items-center gap-1 text-sm text-accent hover:underline"
        >
          {value} <ExternalLink size={12} />
        </a>
      ) : (
        <p className="mt-1 text-sm text-text-primary">{value}</p>
      )}
    </div>
  );
}

const CONSENT_BADGE_STYLES: Record<string, string> = {
  not_requested: "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300",
  pending: "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400",
  given: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400",
  refused: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400",
  withdrawn: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400",
};

const CONSENT_LABELS: Record<string, string> = {
  not_requested: "Not Requested",
  pending: "Pending",
  given: "Given",
  refused: "Refused",
  withdrawn: "Withdrawn",
};

function ConsentBadge({ status }: { status: string }) {
  return (
    <span
      className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${CONSENT_BADGE_STYLES[status] || CONSENT_BADGE_STYLES.not_requested}`}
    >
      {CONSENT_LABELS[status] || status}
    </span>
  );
}
